import {forwardRef, useState} from 'react';
import {Autocomplete, CircularProgress, TextField} from '@mui/material';
import useDebounce from 'ahooks/es/useDebounce';

import {useGetParticipants} from '../../apiHooks';
import {Participant} from '../../types';

type Props = {
  value: Participant | null;
  onChange: (value: Participant | null) => void;
  categorySlug?: string;
  label?: string;
  disabled?: boolean;
};

export const ParticipantSelect = forwardRef<HTMLInputElement, Props>(
  ({value, onChange, categorySlug, label, disabled}, ref) => {
    const [inputValue, setInputValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebounce(searchQuery, {wait: 400});

    const {data, isPending} = useGetParticipants({
      page: 1,
      pageSize: 20,
      categorySlug: categorySlug || undefined,
      searchQuery: debouncedSearch || undefined,
    });

    const options = data?.items ?? [];

    const isValueInOptions = value
      ? options.some(o => o.participantId === value.participantId)
      : true;
    const optionsWithSelected =
      !isValueInOptions && value ? [value, ...options] : options;

    return (
      <Autocomplete
        ref={ref}
        value={value}
        inputValue={inputValue}
        options={optionsWithSelected}
        loading={isPending}
        disabled={disabled}
        filterOptions={x => x}
        getOptionLabel={opt => {
          const name = opt.names[0] ?? opt.participantId;
          return opt.nation ? `${name} (${opt.nation})` : name;
        }}
        isOptionEqualToValue={(opt, val) =>
          opt.participantId === val.participantId
        }
        onInputChange={(_, newInput, reason) => {
          setInputValue(newInput);
          if (reason === 'input') setSearchQuery(newInput);
          else setSearchQuery('');
        }}
        onChange={(_, newValue) => onChange(newValue)}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isPending && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    );
  },
);
